import { useState } from 'react';
import { dramaStatusOptions } from '@/mocks/dramas';

export interface FilterState {
  status: string;
  sort: string;
  favoritesOnly: boolean;
}

interface FilterBarProps {
  filters: FilterState;
  onStatusChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onFavoritesToggle: () => void;
  onReset: () => void;
  resultCount: number;
}

const sortOptions = [
  { value: 'newest', label: '最近更新' },
  { value: 'episodes', label: '集数顺序' },
];

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap ${
            value === option.value
              ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
              : 'text-foreground-600 hover:text-primary-700'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export default function FilterBar({
  filters,
  onStatusChange,
  onSortChange,
  onFavoritesToggle,
  onReset,
  resultCount,
}: FilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const controls = (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-xs text-foreground-500">更新状态</span>
        <Segmented options={dramaStatusOptions} value={filters.status} onChange={onStatusChange} />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs text-foreground-500">排序方式</span>
        <Segmented options={sortOptions} value={filters.sort} onChange={onSortChange} />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs text-foreground-500">其他</span>
        <button
          type="button"
          onClick={onFavoritesToggle}
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm border transition-colors whitespace-nowrap ${
            filters.favoritesOnly
              ? 'bg-accent-500 border-accent-500 text-background-50 dark:text-foreground-950'
              : 'bg-background-100 border-background-200 text-foreground-600 hover:text-accent-600'
          }`}
        >
          <i className={`${filters.favoritesOnly ? 'ri-heart-3-fill' : 'ri-heart-3-line'}`}></i>
          只看收藏
        </button>
      </div>
    </>
  );

  return (
    <div className="w-full rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
      <div className="hidden md:flex flex-wrap items-end gap-x-8 gap-y-4">
        {controls}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-foreground-600">
            共 <strong className="text-primary-700 font-label">{resultCount}</strong> 部剧集
          </span>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border border-background-300 text-foreground-600 hover:border-accent-300 hover:text-accent-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>

      <div className="md:hidden flex items-center justify-between gap-3">
        <span className="text-sm text-foreground-600">
          共 <strong className="text-primary-700 font-label">{resultCount}</strong> 部剧集
        </span>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium whitespace-nowrap"
        >
          <i className="ri-equalizer-line"></i>
          筛选
        </button>
      </div>

      {sheetOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setSheetOpen(false)}
            role="presentation"
          ></div>
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 pb-8 animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-700"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="flex flex-col gap-5">{controls}</div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 h-11 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap"
              >
                重置筛选
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium whitespace-nowrap"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}